import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  AppBar,
  Box,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
  Alert,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FitScreenIcon from "@mui/icons-material/FitScreen";

/**
 * Viewer contract:
 *  - Prefer full PDF via pdfUrl for open/download.
 *  - Render pages using pageImageUrls[] (absolute URLs to /api/docs/{docId}/pages/{n}).
 *  - We fetch each page via axios as a BLOB and display it.
 */
export type DocSource = {
  docId: string;               // NEW: stable id to reset/dedupe on doc change
  name: string;
  pageImageUrls: string[];     // absolute endpoints
  pdfUrl?: string;             // optional: open in new tab / download
  originalFileUrl?: string;    // optional download fallback
  pageCount?: number;          // optional override
  docType?: string;
};

type Props = {
  source: DocSource;
  height?: string | number;
};

type PageCacheItem = { url: string; type: string };

const CustomDocViewEnterpriseSearch: React.FC<Props> = ({ source, height = "100%" }) => {
  const {
    docId,
    name,
    pageImageUrls = [],
    pdfUrl,
    originalFileUrl,
    pageCount: givenCount,
  } = source;

  const endpoints = pageImageUrls;
  const pageCount = useMemo(
    () => (givenCount && givenCount > 0 ? givenCount : endpoints.length),
    [givenCount, endpoints.length]
  );

  const [pageIdx, setPageIdx] = useState(0);
  const [fitWidth, setFitWidth] = useState(true);
  const [scale, setScale] = useState(1);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // Cache for blob object URLs
  const cacheRef = useRef<Map<number, PageCacheItem>>(new Map());
  // In-flight requests to dedupe (prevents duplicate API hits, incl. StrictMode double-runs)
  const inflightRef = useRef<Map<number, Promise<PageCacheItem | null>>>(new Map());
  // Track pending requests to toggle progress bar precisely
  const pendingCountRef = useRef(0);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      cacheRef.current.forEach((v) => URL.revokeObjectURL(v.url));
      cacheRef.current.clear();
      inflightRef.current.clear();
    };
  }, []);

  // Reset viewer state when the document changes
  useEffect(() => {
    // Cleanup old object URLs
    cacheRef.current.forEach((v) => URL.revokeObjectURL(v.url));
    cacheRef.current.clear();
    inflightRef.current.clear();
    pendingCountRef.current = 0;

    setPageIdx(0);
    setScale(1);
    setErr(null);
    setLoading(true);
  }, [docId]);

  // Dedupe + fetch a page blob
  const fetchPage = useCallback(
    async (index: number): Promise<PageCacheItem | null> => {
      if (cacheRef.current.has(index)) return cacheRef.current.get(index)!;
      if (inflightRef.current.has(index)) return inflightRef.current.get(index)!;

      const endpoint = endpoints[index];
      if (!endpoint) return null;

      const controller = new AbortController();

      const p = (async () => {
        try {
          setErr(null);
          pendingCountRef.current += 1;
          setLoading(true);

          const res = await axios.get(endpoint, {
            responseType: "blob",
            signal: controller.signal,
          });
          const blob: Blob = res.data;
          const objUrl = URL.createObjectURL(blob);
          const item = { url: objUrl, type: blob.type || "" };
          cacheRef.current.set(index, item);
          return item;
        } catch (e: any) {
          if (axios.isCancel?.(e) || e?.name === "CanceledError" || e?.name === "AbortError") {
            return null;
          }
          setErr(
            e?.response
              ? `Error ${e.response.status}: ${e.response.statusText}`
              : `Network error: ${e?.message || "unknown"}`
          );
          return null;
        } finally {
          inflightRef.current.delete(index);
          pendingCountRef.current = Math.max(0, pendingCountRef.current - 1);
          if (pendingCountRef.current === 0) setLoading(false);
        }
      })();

      inflightRef.current.set(index, p);
      return p;
    },
    [endpoints]
  );

  // Prefetch next page for smooth nav
  const prefetchNext = useCallback(
    (i: number) => {
      const n = i + 1;
      if (n < pageCount && !cacheRef.current.has(n) && !inflightRef.current.has(n)) {
        void fetchPage(n);
      }
    },
    [fetchPage, pageCount]
  );

  // Load current page on change
  useEffect(() => {
    if (!pageCount) { setLoading(false); return; }
    let cancelled = false;

    (async () => {
      await fetchPage(pageIdx);
      if (!cancelled) prefetchNext(pageIdx);
    })();

    return () => { cancelled = true; };
  }, [pageIdx, pageCount, fetchPage, prefetchNext]);

  const canPrev = pageIdx > 0;
  const canNext = pageIdx < Math.max(0, pageCount - 1);

  const doDownload = () => {
    const url = pdfUrl || originalFileUrl;
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name}.pdf`;
    a.target = "_blank";
    a.click();
  };

  const openPdfNewTab = () => {
    if (pdfUrl) window.open(pdfUrl, "_blank", "noopener,noreferrer");
  };

  const imgStyle: React.CSSProperties = fitWidth
    ? { width: "100%", height: "auto" }
    : { transform: `scale(${scale})`, transformOrigin: "top left" };

  const current = cacheRef.current.get(pageIdx);

  return (
    <Box sx={{ height, display: "flex", flexDirection: "column" }}>
      {/* Toolbar */}
      <AppBar
        position="static"
        color="default"
        elevation={0}
        sx={{ borderBottom: (t) => `1px solid ${t.palette.divider}`, px: 1, py: 0.5 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h3" sx={{ flexGrow: 1 }} noWrap title={name}>
            {name}
          </Typography>

          {pageCount > 0 && (
            <>
              <Tooltip title="Previous page">
                <span>
                  <IconButton size="medium" onClick={() => setPageIdx((i) => Math.max(0, i - 1))} disabled={!canPrev}>
                    <ArrowBackIosNewIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>

              <Typography variant="body2">
                {Math.min(pageIdx + 1, Math.max(1, pageCount))} / {Math.max(1, pageCount)}
              </Typography>

              <Tooltip title="Next page">
                <span>
                  <IconButton size="medium" onClick={() => setPageIdx((i) => Math.min(pageCount - 1, i + 1))} disabled={!canNext}>
                    <ArrowForwardIosIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>

              <Box sx={{ mx: 1 }} />

              <Tooltip title={fitWidth ? "Disable Fit to Width" : "Fit to Width"}>
                <IconButton size="medium" onClick={() => setFitWidth((v) => !v)}>
                  <FitScreenIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>

              {!fitWidth && (
                <>
                  <Tooltip title="Zoom out">
                    <IconButton size="medium" onClick={() => setScale((s) => Math.max(0.25, +(s - 0.1).toFixed(2)))}>
                      <ZoomOutIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Zoom in">
                    <IconButton size="medium" onClick={() => setScale((s) => Math.min(5, +(s + 0.1).toFixed(2)))}>
                      <ZoomInIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </>
          )}

          {/* {pdfUrl && (
            <Tooltip title="Open full PDF in new tab">
              <IconButton size="medium" onClick={openPdfNewTab}>
                <OpenInNewIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}

          {(pdfUrl || originalFileUrl) && (
            <Tooltip title="Download">
              <IconButton size="medium" onClick={doDownload}>
                <DownloadIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )} */}
        </Box>
      </AppBar>

      {loading && <LinearProgress />}

      {err && (
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          {err}
        </Alert>
      )}

      {/* Surface */}
      <Box sx={{ flex: 1, overflow: "auto", bgcolor: "background.default", p: 2 }}>
        {pageCount === 0 ? (
          pdfUrl ? (
            <object
              data={pdfUrl}
              type="application/pdf"
              width="100%"
              height="100%"
              aria-label={name}
              onLoad={() => setLoading(false)}
            >
              <Typography>PDF cannot be displayed.</Typography>
            </object>
          ) : (
            <Typography color="text.secondary">No preview available.</Typography>
          )
        ) : current ? (
          current.type.startsWith("application/pdf") ? (
            <object
              data={current.url}
              type={current.type}
              width="100%"
              height="100%"
              aria-label={`page-${pageIdx + 1}`}
              onLoad={() => setLoading(false)}
            >
              <Typography>PDF page cannot be displayed.</Typography>
            </object>
          ) : (
            <img
              src={current.url}
              alt={`page-${pageIdx + 1}`}
              style={imgStyle}
              onLoad={() => setLoading(false)}
              onError={() => setLoading(false)}
            />
          )
        ) : (
          <Box sx={{ height: 200 }} />
        )}
      </Box>
    </Box>
  );
};

export default CustomDocViewEnterpriseSearch;
